/**
 * DataQualitySection - Data validation and quality control settings
 */

import { useState, useRef, useEffect } from 'react'
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/useToast'
import { AlertTriangle, Shield, TrendingUp } from 'lucide-react'
import { getMinBarThreshold, setMinBarThreshold } from '@/components/ui/low-bar-count-warning-dialog'

const LOW_BAR_WARNING_STORAGE_KEY = 'optimizer_dismiss_low_bar_warning'
const MIN_THRESHOLD = 50
const MAX_THRESHOLD = 10000

interface SettingRowProps {
  icon: React.ReactNode
  title: string
  description: React.ReactNode
  action: React.ReactNode
}

function SettingRow({ icon, title, description, action }: SettingRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-border/30 last:border-0 last:pb-0 first:pt-0">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-muted/50 mt-0.5">
          {icon}
        </div>
        <div className="space-y-1">
          <h4 className="font-medium text-white">{title}</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </div>
      <div className="flex-shrink-0">
        {action}
      </div>
    </div>
  )
}

export function DataQualitySection() {
  const { toast } = useToast()

  const [showLowBarWarning, setShowLowBarWarning] = useState(() => {
    return localStorage.getItem(LOW_BAR_WARNING_STORAGE_KEY) !== 'true'
  })

  const [barThreshold, setBarThreshold] = useState(() => getMinBarThreshold())
  const [isEditingThreshold, setIsEditingThreshold] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditingThreshold && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditingThreshold])

  const handleThresholdClick = () => {
    setEditValue(barThreshold.toString())
    setIsEditingThreshold(true)
  }

  const handleThresholdSave = () => {
    const newValue = parseInt(editValue, 10)
    if (!isNaN(newValue) && newValue >= MIN_THRESHOLD && newValue <= MAX_THRESHOLD) {
      setBarThreshold(newValue)
      setMinBarThreshold(newValue)
      toast({
        title: 'Threshold updated',
        description: `Low bar warning threshold set to ${newValue.toLocaleString()} bars.`,
      })
    } else {
      toast({
        title: 'Invalid value',
        description: `Please enter a number between ${MIN_THRESHOLD} and ${MAX_THRESHOLD.toLocaleString()}.`,
        variant: 'destructive',
      })
    }
    setIsEditingThreshold(false)
  }

  const handleThresholdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleThresholdSave()
    } else if (e.key === 'Escape') {
      setIsEditingThreshold(false)
    }
  }

  const handleLowBarWarningToggle = (enabled: boolean) => {
    setShowLowBarWarning(enabled)
    if (enabled) {
      localStorage.removeItem(LOW_BAR_WARNING_STORAGE_KEY)
      toast({
        title: 'Data quality warnings enabled',
        description: `You'll be warned when uploads have fewer than ${barThreshold.toLocaleString()} bars.`,
      })
    } else {
      localStorage.setItem(LOW_BAR_WARNING_STORAGE_KEY, 'true')
      toast({
        title: 'Data quality warnings disabled',
        description: 'Low bar count warnings won\'t appear during uploads.',
      })
    }
  }

  return (
    <div className="space-y-6">
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Shield className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <GlassCardTitle className="text-lg">Data Validation</GlassCardTitle>
              <GlassCardDescription>
                Control how data quality is validated during uploads
              </GlassCardDescription>
            </div>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="space-y-0">
            <SettingRow
              icon={<AlertTriangle className="h-4 w-4 text-amber-400" />}
              title="Low bar count warning"
              description={
                <>
                  Warn when uploads have fewer than{' '}
                  {isEditingThreshold ? (
                    <input
                      ref={inputRef}
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleThresholdSave}
                      onKeyDown={handleThresholdKeyDown}
                      min={MIN_THRESHOLD}
                      max={MAX_THRESHOLD}
                      className="w-20 px-1.5 py-0.5 text-sm bg-dark-800 border border-brand-purple rounded text-white font-medium text-center focus:outline-none focus:ring-1 focus:ring-brand-purple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  ) : (
                    <button
                      onClick={handleThresholdClick}
                      className="inline-flex px-1.5 py-0.5 text-sm font-medium text-white bg-dark-800/50 hover:bg-dark-700 border border-dark-600 hover:border-brand-purple/50 rounded cursor-pointer transition-colors"
                      title="Click to edit threshold"
                    >
                      {barThreshold.toLocaleString()}
                    </button>
                  )}{' '}
                  bars. This helps ensure statistical significance in your optimization results.
                </>
              }
              action={
                <Switch
                  checked={showLowBarWarning}
                  onCheckedChange={handleLowBarWarningToggle}
                />
              }
            />

            <SettingRow
              icon={<TrendingUp className="h-4 w-4 text-green-400" />}
              title="Statistical significance checks"
              description="Automatically flag results that may not have enough data points for reliable conclusions."
              action={
                <Switch
                  checked={true}
                  disabled
                  className="opacity-50"
                />
              }
            />
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Info card */}
      <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-blue-400">Tip:</span> For reliable optimization results,
          we recommend datasets with at least 500 bars. Results from smaller datasets may be prone to
          overfitting and should be validated on out-of-sample data.
        </p>
      </div>
    </div>
  )
}
